import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Admin User interface
interface AdminUser {
  admin_id: string;
  username: string;
  email: string;
}

// Admin auth state interface
interface AdminAuthState {
  adminUser: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => void;
}

// Session token key for secure storage
const SESSION_TOKEN_KEY = 'admin_session_token';

export const useSecureAdminAuth = (): AdminAuthState => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    console.log("useSecureAdminAuth: Starting secure login process for username:", username);
    setIsLoading(true);

    try {
      // Get client IP and user agent for security logging
      const userAgent = navigator.userAgent;
      
      // Call the new secure admin login function
      const { data, error } = await supabase.rpc('secure_admin_login', {
        input_username: username,
        input_password: password,
        client_user_agent: userAgent
      });

      console.log("Secure login response:", { data, error });

      if (error) {
        console.error("Login error:", error);
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: "Invalid username or password.",
        });
        return false;
      }

      if (data && data.length > 0) {
        const loginResult = data[0];
        console.log("Login successful, session data:", loginResult);
        
        const admin: AdminUser = {
          admin_id: loginResult.admin_user.admin_id,
          username: loginResult.admin_user.username,
          email: loginResult.admin_user.email
        };

        setAdminUser(admin);

        // Store secure session token in localStorage
        localStorage.setItem(SESSION_TOKEN_KEY, loginResult.session_token);
        
        toast({
          title: "Login Successful",
          description: `Welcome back, ${admin.username}!`,
        });

        return true;
      } else {
        console.log("No session data returned");
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: "Invalid username or password.",
        });
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        variant: "destructive",
        title: "Login Error",
        description: "An error occurred during login. Please try again.",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const logout = useCallback(async () => {
    console.log("useSecureAdminAuth: Logging out admin");
    
    // Get session token to revoke
    const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
    
    if (sessionToken) {
      try {
        // Use enhanced revocation with detailed logging
        const userAgent = navigator.userAgent;
        
        await supabase.rpc('secure_revoke_admin_session', {
          session_token: sessionToken,
          revoke_reason: 'manual_logout',
          client_user_agent: userAgent
        });
        
        console.log("Session revoked successfully");
      } catch (error) {
        console.error("Error revoking session:", error);
        // Continue logout even if revocation fails
      }
    }
    
    setAdminUser(null);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    
    toast({
      title: "Logged Out",
      description: "You have been securely logged out.",
    });
  }, [toast]);

  const checkAuth = useCallback(async () => {
    console.log("useSecureAdminAuth: Checking authentication status");
    setIsLoading(true);
    
    try {
      const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
      
      if (!sessionToken) {
        console.log("No session token found");
        setIsLoading(false);
        return;
      }

      // Use enhanced session verification with security checks
      const userAgent = navigator.userAgent;
      
      let { data, error } = await supabase.rpc('secure_verify_admin_session', {
        session_token: sessionToken,
        client_user_agent: userAgent
      });
      
      // Fallback to simpler verifier if secure function errors
      if (error) {
        console.warn("secure_verify_admin_session failed, falling back to verify_admin_session:", error);
        const fallback = await supabase.rpc('verify_admin_session', {
          session_token: sessionToken
        });
        data = fallback.data;
        error = fallback.error;
      }
      
      if (error) {
        console.error("Session verification error:", error);
        localStorage.removeItem(SESSION_TOKEN_KEY);
        toast({
          variant: "destructive",
          title: "Session Error",
          description: "Your session could not be verified. Please log in again.",
        });
        setIsLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const verificationResult = data[0];
        
        if (verificationResult.is_valid && verificationResult.admin_user) {
          const admin: AdminUser = {
            admin_id: verificationResult.admin_user.admin_id,
            username: verificationResult.admin_user.username,
            email: verificationResult.admin_user.email
          };
          
          console.log("Valid session, setting admin user:", admin);
          setAdminUser(admin);
        } else {
          console.log("Session is invalid");
          localStorage.removeItem(SESSION_TOKEN_KEY);
        }
      } else {
        console.log("No verification data returned");
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
      
    } catch (error) {
      console.error("Error checking auth:", error);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: "An error occurred while checking your session.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Initialize authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Enhanced periodic session validation with security checks
  useEffect(() => {
    const validateSession = async () => {
      const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
      
      if (sessionToken && adminUser) {
        try {
          // Use enhanced session verification
          const userAgent = navigator.userAgent;
          
          let { data, error } = await supabase.rpc('secure_verify_admin_session', {
            session_token: sessionToken,
            client_user_agent: userAgent
          });
          
          // Fallback if secure verifier errors
          if (error) {
            console.warn("secure_verify_admin_session failed during interval check, falling back:", error);
            const fb = await supabase.rpc('verify_admin_session', { session_token: sessionToken });
            data = fb.data;
            error = fb.error;
          }
          
          if (error || !data || data.length === 0 || !data[0].is_valid) {
            console.log("Session validation failed, logging out");
            toast({
              variant: "destructive",
              title: "Session Expired",
              description: "Your session has expired. Please log in again.",
            });
            logout();
          }
        } catch (error) {
          console.error("Error validating session:", error);
          toast({
            variant: "destructive",
            title: "Session Error",
            description: "There was an error validating your session.",
          });
          logout();
        }
      }
    };

    // Validate session more frequently (every 5 minutes) for better security
    const interval = adminUser ? setInterval(validateSession, 5 * 60 * 1000) : null;
    
    // Also validate on window focus to detect session changes
    const handleFocus = () => {
      if (adminUser) {
        validateSession();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      if (interval) clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [adminUser, logout, toast]);

  return {
    adminUser,
    isAuthenticated: !!adminUser,
    isLoading,
    login,
    logout,
    checkAuth
  };
};